import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Newspaper, Loader2, ArrowLeft, Mail, Phone, User, 
  Calendar, FileText, RefreshCw, ChevronDown, ChevronUp,
  Check, Clock, Archive
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPressPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [inquiries, setInquiries] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');

  const isAdmin = user?.email === 'oanjorin@anjolc.com' || user?.is_admin;

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, isAdmin, navigate]);

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      fetchInquiries();
    }
  }, [isAuthenticated, isAdmin, filter]);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      const res = await fetch(`${API_URL}/api/press/inquiries${statusParam}`);
      
      if (res.ok) {
        const data = await res.json();
        setInquiries(data.inquiries || []);
      }
    } catch (error) {
      console.error('Failed to fetch inquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (inquiryId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/press/inquiries/${inquiryId}/status?status=${newStatus}`, {
        method: 'PATCH'
      });
      
      if (res.ok) {
        fetchInquiries();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      contacted: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      in_progress: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      archived: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[status] || styles.new}`}>
        {status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1)}
      </span>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050810]">
        <Header />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
          <span className="ml-3 text-gray-400">Loading press inquiries...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810]">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="text-gray-400 hover:text-white mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin Panel
        </Button>

        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#F97316]/20 rounded-xl">
              <Newspaper className="h-6 w-6 text-[#F97316]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Press Inquiries</h1>
              <p className="text-gray-400 text-sm">View and manage press inquiry submissions</p>
            </div>
          </div>
          <Button
            onClick={fetchInquiries}
            variant="outline"
            className="border-[#1e2a45] text-gray-300"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4">
            <div className="text-blue-400 text-sm mb-1">New</div>
            <span className="text-2xl font-bold text-white">
              {inquiries.filter(i => i.status === 'new').length}
            </span>
          </div>
          <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4">
            <div className="text-yellow-400 text-sm mb-1">Contacted</div>
            <span className="text-2xl font-bold text-white">
              {inquiries.filter(i => i.status === 'contacted').length}
            </span>
          </div>
          <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4">
            <div className="text-purple-400 text-sm mb-1">In Progress</div>
            <span className="text-2xl font-bold text-white">
              {inquiries.filter(i => i.status === 'in_progress').length}
            </span>
          </div>
          <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4">
            <div className="text-green-400 text-sm mb-1">Completed</div>
            <span className="text-2xl font-bold text-white">
              {inquiries.filter(i => i.status === 'completed').length}
            </span>
          </div>
          <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4">
            <div className="text-gray-400 text-sm mb-1">Total</div>
            <span className="text-2xl font-bold text-white">{inquiries.length}</span>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['all', 'new', 'contacted', 'in_progress', 'completed', 'archived'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'ghost'}
              onClick={() => setFilter(f)}
              size="sm"
              className={filter === f 
                ? 'bg-[#3B82F6] text-white' 
                : 'text-gray-400 hover:text-white'}
            >
              {f === 'all' ? 'All' : f.replace('_', ' ').charAt(0).toUpperCase() + f.replace('_', ' ').slice(1)}
            </Button>
          ))}
        </div>

        {/* Inquiries List */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] overflow-hidden">
          {inquiries.length === 0 ? (
            <div className="p-8 text-center">
              <Newspaper className="h-12 w-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No press inquiries found</p>
            </div>
          ) : (
            <div className="divide-y divide-[#1e2a45]">
              {inquiries.map((inquiry) => (
                <div 
                  key={inquiry.id}
                  className="p-4 hover:bg-[#1e2a45]/30 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <User className="h-4 w-4 text-gray-500" />
                        <span className="text-white font-medium">
                          {inquiry.firstName} {inquiry.lastName}
                        </span>
                        {getStatusBadge(inquiry.status)}
                        {!inquiry.email_sent && (
                          <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded">
                            Email not sent
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-400 mb-2">
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          <a href={`mailto:${inquiry.email}`} className="text-[#3B82F6] hover:underline">
                            {inquiry.email}
                          </a>
                        </span>
                        {inquiry.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {inquiry.phone}
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-gray-500">
                          <Calendar className="h-3 w-3" />
                          {new Date(inquiry.created_at).toLocaleDateString()} {new Date(inquiry.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedId(expandedId === inquiry.id ? null : inquiry.id)}
                        className="text-gray-400 hover:text-white p-0 h-auto"
                      >
                        {expandedId === inquiry.id ? (
                          <>
                            <ChevronUp className="h-4 w-4 mr-1" />
                            Hide Details
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-4 w-4 mr-1" />
                            View Reason
                          </>
                        )}
                      </Button>
                      
                      {expandedId === inquiry.id && (
                        <div className="mt-3 p-4 bg-[#1e2a45]/50 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            Reason for Inquiry
                          </h4>
                          <p className="text-gray-400 whitespace-pre-wrap">{inquiry.reason}</p>
                          <p className="text-xs text-gray-600 mt-3">ID: {inquiry.id}</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      {inquiry.status === 'new' && (
                        <Button
                          size="sm"
                          onClick={() => updateStatus(inquiry.id, 'contacted')}
                          className="bg-yellow-600 hover:bg-yellow-700 text-white"
                        >
                          <Mail className="h-4 w-4 mr-1" />
                          Mark Contacted
                        </Button>
                      )}
                      {inquiry.status === 'contacted' && (
                        <Button
                          size="sm"
                          onClick={() => updateStatus(inquiry.id, 'in_progress')}
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                        >
                          <Clock className="h-4 w-4 mr-1" />
                          In Progress
                        </Button>
                      )}
                      {inquiry.status === 'in_progress' && (
                        <Button
                          size="sm"
                          onClick={() => updateStatus(inquiry.id, 'completed')}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Complete
                        </Button>
                      )}
                      {inquiry.status !== 'archived' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateStatus(inquiry.id, 'archived')}
                          className="border-gray-600 text-gray-400 hover:bg-gray-700"
                        >
                          <Archive className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminPressPage;
